import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { 
    Loader2, AlertTriangle, CheckCircle, EyeOff, Trash2, RefreshCw, MapPin, 
    User, Calendar, Shield, Star, Clock, Camera, FileText, ShieldCheck, 
    ChevronLeft, ChevronRight, ExternalLink, Ban, AlertCircle, Mail,
    Eye, Download, X, MessageSquare, History
} from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getErrorMessage = (err) => {
    try {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(e => e?.msg || 'Error').join('. ');
        return err?.message || 'An error occurred';
    } catch { return 'An error occurred'; }
};

// Evidence Viewer Modal
const EvidenceViewer = ({ evidence, onClose }) => {
    if (!evidence) return null;
    
    const isImage = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    const isPDF = (url) => /\.pdf$/i.test(url);
    
    return (
        <Dialog open={!!evidence} onOpenChange={() => onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                <DialogHeader>
                    <DialogTitle>Evidence Preview</DialogTitle>
                    <DialogDescription>
                        Review submitted evidence for this review
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    {evidence.photo_url && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Camera className="h-4 w-4" /> Photo Evidence
                            </Label>
                            {isImage(evidence.photo_url) ? (
                                <img 
                                    src={`${API_URL}${evidence.photo_url}`} 
                                    alt="Evidence" 
                                    className="max-w-full rounded-lg border border-border"
                                />
                            ) : (
                                <a 
                                    href={`${API_URL}${evidence.photo_url}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-primary hover:underline"
                                >
                                    <ExternalLink className="h-4 w-4" /> View Photo
                                </a>
                            )}
                        </div>
                    )}
                    {evidence.document_url && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <FileText className="h-4 w-4" /> Document Evidence
                            </Label>
                            {isPDF(evidence.document_url) ? (
                                <iframe 
                                    src={`${API_URL}${evidence.document_url}`}
                                    className="w-full h-96 border border-border rounded-lg"
                                    title="Document Preview"
                                />
                            ) : (
                                <a 
                                    href={`${API_URL}${evidence.document_url}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-primary hover:underline"
                                >
                                    <Download className="h-4 w-4" /> Download Document
                                </a>
                            )}
                        </div>
                    )}
                    {evidence.job_confirmation && (
                        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <div className="flex items-center gap-2 text-emerald-600">
                                <CheckCircle className="h-5 w-5" />
                                <span className="font-medium">Job Confirmation Provided</span>
                            </div>
                        </div>
                    )}
                    {!evidence.photo_url && !evidence.document_url && !evidence.job_confirmation && (
                        <p className="text-muted-foreground text-center py-8">No evidence files attached</p>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// User Strike History Panel
const StrikeHistoryPanel = ({ userId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [addingStrike, setAddingStrike] = useState(false);
    const [strikeReason, setStrikeReason] = useState('');

    useEffect(() => {
        if (userId) loadHistory();
    }, [userId]);

    const loadHistory = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getStrikeHistory(userId);
            setData(res.data);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleAddStrike = async () => {
        try {
            await adminAPI.addStrike(userId, strikeReason);
            toast.success('Strike added');
            setStrikeReason('');
            setAddingStrike(false);
            loadHistory();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleRemoveStrike = async () => {
        try {
            await adminAPI.removeStrike(userId);
            toast.success('Strike removed');
            loadHistory();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleSuspend = async () => {
        if (!window.confirm('Are you sure you want to suspend this user?')) return;
        try {
            await adminAPI.suspendUser(userId, 'Suspended via moderation panel');
            toast.success('User suspended');
            loadHistory();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    return (
        <Dialog open={!!userId} onOpenChange={() => onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <History className="h-5 w-5" /> Contractor Moderation History
                    </DialogTitle>
                </DialogHeader>
                
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : data ? (
                    <div className="space-y-6">
                        {/* User Info */}
                        <div className="p-4 bg-accent/50 rounded-lg">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="font-semibold text-foreground">{data.user.company_name || 'Unknown'}</p>
                                    <p className="text-sm text-muted-foreground">{data.user.email}</p>
                                </div>
                                <div className="text-right">
                                    <Badge className={data.user.moderation_strikes > 0 ? 'bg-red-600' : 'bg-emerald-600'}>
                                        {data.user.moderation_strikes} Strike{data.user.moderation_strikes !== 1 ? 's' : ''}
                                    </Badge>
                                    {data.user.suspended && (
                                        <Badge className="bg-red-800 ml-2">Suspended</Badge>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                            <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => setAddingStrike(true)}
                            >
                                <AlertCircle className="h-4 w-4 mr-1" /> Add Strike
                            </Button>
                            {data.user.moderation_strikes > 0 && (
                                <Button size="sm" variant="outline" onClick={handleRemoveStrike}>
                                    Remove Strike
                                </Button>
                            )}
                            {!data.user.suspended && (
                                <Button size="sm" variant="destructive" onClick={handleSuspend}>
                                    <Ban className="h-4 w-4 mr-1" /> Suspend Account
                                </Button>
                            )}
                        </div>

                        {/* Add Strike Form */}
                        {addingStrike && (
                            <div className="p-4 border border-border rounded-lg space-y-3">
                                <Label>Strike Reason</Label>
                                <Textarea 
                                    value={strikeReason}
                                    onChange={(e) => setStrikeReason(e.target.value)}
                                    placeholder="Enter reason for strike..."
                                    rows={2}
                                />
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={handleAddStrike}>Confirm Strike</Button>
                                    <Button size="sm" variant="outline" onClick={() => setAddingStrike(false)}>Cancel</Button>
                                </div>
                            </div>
                        )}

                        {/* Strike History */}
                        <div>
                            <h3 className="font-semibold mb-3">Strike History</h3>
                            {data.strike_history.length === 0 ? (
                                <p className="text-muted-foreground text-sm">No strike history</p>
                            ) : (
                                <div className="space-y-2">
                                    {data.strike_history.map((log, i) => (
                                        <div key={i} className="p-3 bg-background border border-border rounded-lg text-sm">
                                            <div className="flex items-center justify-between">
                                                <Badge className={log.action.includes('added') ? 'bg-red-600' : 'bg-emerald-600'}>
                                                    {log.action.includes('added') ? 'Strike Added' : 'Strike Removed'}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">
                                                    {formatDate(log.timestamp)}
                                                </span>
                                            </div>
                                            {log.reason && (
                                                <p className="mt-2 text-muted-foreground">Reason: {log.reason}</p>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1">
                                                By: {log.admin_email || 'Admin'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Recent Reviews */}
                        <div>
                            <h3 className="font-semibold mb-3">Recent Reviews ({data.reviews.length})</h3>
                            {data.reviews.length === 0 ? (
                                <p className="text-muted-foreground text-sm">No reviews submitted</p>
                            ) : (
                                <div className="space-y-2 max-h-48 overflow-auto">
                                    {data.reviews.slice(0, 10).map((review) => (
                                        <div key={review.id} className="p-2 bg-background border border-border rounded text-sm flex justify-between items-center">
                                            <div>
                                                <p className="font-medium">{review.formatted_address || 'Unknown Address'}</p>
                                                <p className="text-xs text-muted-foreground">{formatDate(review.created_at)}</p>
                                            </div>
                                            <Badge className={
                                                review.status === 'approved' ? 'bg-emerald-600' :
                                                review.status === 'flagged' ? 'bg-amber-600' :
                                                review.status === 'rejected' ? 'bg-red-600' : 'bg-gray-600'
                                            }>
                                                {review.status}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
                
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const AdminModeration = () => {
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
    const [actionDialog, setActionDialog] = useState({ open: false, action: '', review: null });
    const [evidenceViewer, setEvidenceViewer] = useState(null);
    const [strikeHistoryUser, setStrikeHistoryUser] = useState(null);
    const [requestEvidenceDialog, setRequestEvidenceDialog] = useState({ open: false, review: null });
    const [evidenceMessage, setEvidenceMessage] = useState('Please provide additional evidence for your review.');

    const loadPendingReviews = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const res = await adminAPI.getPendingReviewsPaginated(page, pagination.limit);
            setReviews(res.data.reviews || []);
            setPagination(res.data.pagination);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [pagination.limit]);

    useEffect(() => { loadPendingReviews(); }, []);

    const handleAction = async (action, review) => {
        try {
            if (action === 'approve') {
                await adminAPI.approveReview(review.id);
                toast.success('Review approved');
            } else if (action === 'reject') {
                await adminAPI.rejectReview(review.id);
                toast.success('Review rejected');
            } else if (action === 'hide') {
                await adminAPI.hideReview(review.id);
                toast.success('Review hidden');
            } else if (action === 'delete') {
                await adminAPI.deleteReview(review.id);
                toast.success('Review deleted');
            }
            loadPendingReviews(pagination.page);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
        setActionDialog({ open: false, action: '', review: null });
    };

    const handleRequestEvidence = async () => {
        try {
            await adminAPI.requestEvidence(requestEvidenceDialog.review.id, evidenceMessage);
            toast.success('Evidence request sent');
            setRequestEvidenceDialog({ open: false, review: null });
            setEvidenceMessage('Please provide additional evidence for your review.');
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleSuspendUser = async (userId) => {
        if (!window.confirm('Are you sure you want to suspend this user?')) return;
        try {
            await adminAPI.suspendUser(userId, 'Suspended from moderation queue');
            toast.success('User suspended');
            loadPendingReviews(pagination.page);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    if (loading && reviews.length === 0) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground">Moderation Queue</h1>
                        <p className="text-muted-foreground">Flagged reviews requiring manual approval</p>
                    </div>
                    <Button onClick={() => loadPendingReviews(pagination.page)} variant="outline" size="sm" disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>

                {/* Queue Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-red-500/10 border-red-500/30">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-red-500">{pagination.total}</p>
                            <p className="text-sm text-muted-foreground">Total Flagged</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-amber-500/10 border-amber-500/30">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-amber-500">{reviews.length}</p>
                            <p className="text-sm text-muted-foreground">This Page</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border col-span-2">
                        <CardContent className="p-4 text-center">
                            <p className="text-sm text-muted-foreground">
                                Reviews auto-approve if they pass content filters and contractor has no moderation strikes
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Queue List */}
                <Card className="bg-card border-border">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Review Queue
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {reviews.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                                <p className="text-lg font-medium">All caught up!</p>
                                <p className="text-sm">No reviews requiring moderation</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {reviews.map((review) => (
                                    <div key={review.id} className={`p-4 rounded-lg border ${
                                        review.status === 'flagged' ? 'bg-red-500/5 border-red-500/30' : 'bg-amber-500/5 border-amber-500/30'
                                    }`}>
                                        <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0 space-y-3">
                                                {/* Review ID & Status */}
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                                                        {review.id.slice(0, 8)}...
                                                    </code>
                                                    <Badge className={review.status === 'flagged' ? 'bg-red-600' : 'bg-amber-600'}>
                                                        {review.status}
                                                    </Badge>
                                                    {review.flagged_reason && (
                                                        <span className="text-xs text-red-500">
                                                            {review.flagged_reason}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {/* Address - Using formatted_address from Mapbox */}
                                                <div className="flex items-start gap-2">
                                                    <MapPin className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <p className="font-medium text-foreground">
                                                            {review.formatted_address || 'Unknown Address'}
                                                        </p>
                                                        {review.latitude && review.longitude && (
                                                            <p className="text-xs text-muted-foreground">
                                                                {review.latitude.toFixed(4)}, {review.longitude.toFixed(4)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Contractor Info Card */}
                                                {review.contractor && (
                                                    <div className="p-3 bg-background/50 rounded-lg border border-border/50">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-xs text-muted-foreground font-medium">Contractor Details</p>
                                                            <Button 
                                                                size="sm" 
                                                                variant="ghost" 
                                                                className="h-6 text-xs"
                                                                onClick={() => setStrikeHistoryUser(review.contractor.id)}
                                                            >
                                                                <History className="h-3 w-3 mr-1" /> History
                                                            </Button>
                                                        </div>
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                                            <div className="flex items-center gap-1">
                                                                <User className="h-3 w-3 text-muted-foreground" />
                                                                <span className="text-foreground font-medium truncate">
                                                                    {review.contractor.company_name}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <Shield className="h-3 w-3 text-muted-foreground" />
                                                                <span className="text-foreground">{review.contractor.rank_name}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <Clock className="h-3 w-3 text-muted-foreground" />
                                                                <span className="text-foreground">{review.contractor.account_age_days}d old</span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <Star className="h-3 w-3 text-muted-foreground" />
                                                                <span className="text-foreground">{review.contractor.approved_reviews} reviews</span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-muted-foreground">Strikes:</span>
                                                                <span className={review.contractor.moderation_strikes > 0 ? 'text-red-500 font-medium' : 'text-foreground'}>
                                                                    {review.contractor.moderation_strikes}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <ShieldCheck className="h-3 w-3 text-muted-foreground" />
                                                                <span className="text-foreground capitalize">{review.contractor.verification_tier}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Review Tags */}
                                                {review.tags && review.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1">
                                                        {review.tags.map((tag, i) => (
                                                            <Badge key={i} variant="outline" className="text-xs">
                                                                {tag}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Evidence Summary */}
                                                <div className="flex items-center gap-3 text-xs flex-wrap">
                                                    <span className="text-muted-foreground">Evidence:</span>
                                                    {review.job_confirmation && (
                                                        <span className="flex items-center gap-1 text-emerald-600">
                                                            <CheckCircle className="h-3 w-3" /> Job Confirmed
                                                        </span>
                                                    )}
                                                    {review.photo_url && (
                                                        <span className="flex items-center gap-1 text-blue-600">
                                                            <Camera className="h-3 w-3" /> Photo
                                                        </span>
                                                    )}
                                                    {review.document_url && (
                                                        <span className="flex items-center gap-1 text-purple-600">
                                                            <FileText className="h-3 w-3" /> Document
                                                        </span>
                                                    )}
                                                    {(review.photo_url || review.document_url || review.job_confirmation) && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="ghost" 
                                                            className="h-6 text-xs text-primary"
                                                            onClick={() => setEvidenceViewer({
                                                                photo_url: review.photo_url,
                                                                document_url: review.document_url,
                                                                job_confirmation: review.job_confirmation
                                                            })}
                                                        >
                                                            <Eye className="h-3 w-3 mr-1" /> View
                                                        </Button>
                                                    )}
                                                    {!review.photo_url && !review.document_url && !review.job_confirmation && (
                                                        <span className="text-muted-foreground">None provided</span>
                                                    )}
                                                </div>

                                                {/* Submission Time */}
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Calendar className="h-3 w-3" />
                                                    Submitted: {formatDate(review.created_at)}
                                                </div>

                                                {/* Notes */}
                                                {review.notes && (
                                                    <div className="p-3 bg-background rounded border border-border">
                                                        <p className="text-xs text-muted-foreground mb-1">Notes:</p>
                                                        <p className="text-sm text-foreground">"{review.notes}"</p>
                                                    </div>
                                                )}

                                                {/* Ratings */}
                                                {review.ratings && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {Object.entries(review.ratings).map(([key, value]) => (
                                                            <span key={key} className="text-xs bg-accent px-2 py-1 rounded">
                                                                {key.replace(/_/g, ' ')}: {value}/5
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Actions */}
                                            <div className="flex flex-wrap gap-2 xl:flex-col xl:min-w-[140px]">
                                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                                                    onClick={() => setActionDialog({ open: true, action: 'approve', review })}>
                                                    <CheckCircle className="h-4 w-4 mr-1" /> Approve
                                                </Button>
                                                <Button size="sm" variant="outline"
                                                    onClick={() => setActionDialog({ open: true, action: 'reject', review })}>
                                                    Reject
                                                </Button>
                                                <Button size="sm" variant="outline"
                                                    onClick={() => setActionDialog({ open: true, action: 'hide', review })}>
                                                    <EyeOff className="h-4 w-4 mr-1" /> Hide
                                                </Button>
                                                <Button size="sm" variant="outline"
                                                    onClick={() => setRequestEvidenceDialog({ open: true, review })}>
                                                    <MessageSquare className="h-4 w-4 mr-1" /> Request Evidence
                                                </Button>
                                                <Button size="sm" variant="destructive"
                                                    onClick={() => setActionDialog({ open: true, action: 'delete', review })}>
                                                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                                                </Button>
                                                {review.contractor && (
                                                    <Button size="sm" variant="destructive" className="bg-red-800"
                                                        onClick={() => handleSuspendUser(review.contractor.id)}>
                                                        <Ban className="h-4 w-4 mr-1" /> Suspend User
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                                <p className="text-sm text-muted-foreground">
                                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                                </p>
                                <div className="flex gap-2">
                                    <Button 
                                        size="sm" 
                                        variant="outline" 
                                        disabled={pagination.page <= 1}
                                        onClick={() => loadPendingReviews(pagination.page - 1)}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        disabled={pagination.page >= pagination.total_pages}
                                        onClick={() => loadPendingReviews(pagination.page + 1)}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Action Dialog */}
            <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, action: '', review: null })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {actionDialog.action === 'approve' && 'Approve Review'}
                            {actionDialog.action === 'reject' && 'Reject Review'}
                            {actionDialog.action === 'hide' && 'Hide Review'}
                            {actionDialog.action === 'delete' && 'Delete Review'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {actionDialog.action === 'approve' && 'This will approve and publish the review. It will affect the property risk score.'}
                            {actionDialog.action === 'reject' && 'This will reject the review and notify the contractor.'}
                            {actionDialog.action === 'hide' && 'This will hide the review from public view while preserving it for records.'}
                            {actionDialog.action === 'delete' && 'This will permanently delete the review. This cannot be undone.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={() => handleAction(actionDialog.action, actionDialog.review)}
                            className={actionDialog.action === 'delete' ? 'bg-destructive hover:bg-destructive/90' : 
                                       actionDialog.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                        >
                            Confirm {actionDialog.action}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Request Evidence Dialog */}
            <Dialog open={requestEvidenceDialog.open} onOpenChange={(open) => !open && setRequestEvidenceDialog({ open: false, review: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Request Additional Evidence</DialogTitle>
                        <DialogDescription>
                            Send a message to the contractor requesting more evidence
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Message</Label>
                            <Textarea 
                                value={evidenceMessage}
                                onChange={(e) => setEvidenceMessage(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRequestEvidenceDialog({ open: false, review: null })}>
                            Cancel
                        </Button>
                        <Button onClick={handleRequestEvidence}>
                            <Mail className="h-4 w-4 mr-2" /> Send Request
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Evidence Viewer */}
            <EvidenceViewer evidence={evidenceViewer} onClose={() => setEvidenceViewer(null)} />

            {/* Strike History Panel */}
            <StrikeHistoryPanel userId={strikeHistoryUser} onClose={() => setStrikeHistoryUser(null)} />
        </AdminLayout>
    );
};

export default AdminModeration;
